import { createReducer, createAction, Action, PayloadAction } from '@reduxjs/toolkit'

interface WebSocketMessage {
  to: string
  type: string
  data: string
}

export interface TutorChannelReducer {
  websocketConnectionMessage: WebSocketMessage
  studentConnectionState: string
  tutorConnectionState: string
  connectionState: string
  isStudentPresent: boolean
  isStudentPresentOnLessonScreen: boolean
  webRtcMounted: boolean
  selectedMicrophoneId: string
  selectedCameraId: string
  startConnection: boolean
}

const initialState: TutorChannelReducer = {
  websocketConnectionMessage: { to: '', type: '', data: '' },
  studentConnectionState: '',
  tutorConnectionState: '',
  connectionState: '',
  isStudentPresent: false,
  isStudentPresentOnLessonScreen: false,
  webRtcMounted: true,
  selectedMicrophoneId: '',
  selectedCameraId: '',
  startConnection: false
}

export type WebSocketMessageActionType = PayloadAction<WebSocketMessage>
export const HANDLE_WS_MESSAGE = createAction<WebSocketMessage>('tutor_channel_reducer/handle_ws_message')
export const CLEAR_CONNECTION_MESSAGE = createAction('tutor_channel_reducer/clear_connection_message')

interface SetWebRtcMountedPayload {
  value: boolean
}
export const SET_WEB_RTC_MOUNTED = createAction<SetWebRtcMountedPayload>('tutor_channel_reducer/set_web_rtc_mounted')

interface SetPresencePayload {
  student: 'true' | 'false'
  tutor: 'true' | 'false'
}
export const SET_PRESENCE = createAction<SetPresencePayload>('tutor_channel_reducer/set_presence')

interface SetPresenceOnLessonScreenPayload {
  student: 'true' | 'false'
  tutor: 'true' | 'false'
}
export const SET_PRESENCE_ON_LESSON_SCREEN = createAction<SetPresenceOnLessonScreenPayload>(
  'tutor_channel_reducer/set_presence_on_lesson_screen'
)

interface SetSelectedDevicesPayload {
  cameraId: string
  microphoneId: string
}
export const SET_SELECTED_DEVICES = createAction<SetSelectedDevicesPayload>(
  'tutor_channel_reducer/set_selected_devices'
)

export const HANDLE_PRESENCE_REPORT = createAction<any>('tutor_channel_reducer/handle_presence_report')

export const RESET_CONNECTION_STATE = createAction('tutor_channel_reducer/reset_connection_state')

export const tutorChannelReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(HANDLE_WS_MESSAGE, (state, action) => {
      if (action.payload?.to === 'tutor') {
        return { ...state, websocketConnectionMessage: action.payload }
      }
      if (action.payload?.to === 'both') {
        if (action.payload?.type === 'student-connection-state') {
          return { ...state, studentConnectionState: action.payload.data }
        }
        if (action.payload?.type === 'tutor-connection-state') {
          return { ...state, tutorConnectionState: action.payload.data }
        }
        if (action.payload?.type === 'connection-state') {
          return { ...state, connectionState: action.payload.data }
        }
        if (action.payload?.type === 'start-connection') {
          return { ...state, startConnection: true }
        }
      }
      return { ...state }
    })
    .addCase(CLEAR_CONNECTION_MESSAGE, (state) => {
      return { ...state, websocketConnectionMessage: { to: '', type: '', data: '' } }
    })
    .addCase(SET_WEB_RTC_MOUNTED, (state, { payload }) => {
      return { ...state, webRtcMounted: payload.value }
    })
    .addCase(SET_PRESENCE, (state, { payload }) => {
      const isStudentPresent = payload.student === 'true'
      return { ...state, isStudentPresent }
    })
    .addCase(SET_SELECTED_DEVICES, (state, { payload }) => {
      return { ...state, selectedCameraId: payload.cameraId, selectedMicrophoneId: payload.microphoneId }
    })
    .addCase(SET_PRESENCE_ON_LESSON_SCREEN, (state, { payload }) => {
      const isStudentPresentOnLessonScreen = payload.student === 'true'
      return { ...state, isStudentPresentOnLessonScreen }
    })
    .addCase(RESET_CONNECTION_STATE, (state) => {
      return {
        ...state,
        tutorConnectionState: '',
        studentConnectionState: '',
        connectionState: '',
        isStudentPresentOnLessonScreen: false,
        startConnection: false
      }
    })
})
